import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty, sortBy } from 'lodash';
import { getSnapshotQaSpecClaims } from 'services/product';
import { generateFormValuesClaims } from 'pages/qa-spec/components/qa-claims/utils';
import { useGetEnumOptions } from 'pages/qa-spec/hooks';
import { renderPopoverContent } from 'pages/qa-spec/utils';

const getQueryKeyQaClaims = (productId, productType) => {
  return ['pim', 'snapshot', parseInt(productId), productType, 'qa-claim'];
};

export const useGetSnapshotQaClaims = ({
  enabled,
  productId,
  productType,
  ...restProps
}) => {
  const queryClient = useQueryClient();

  const getSnapshotQaClaimsData = async () => {
    const response = await getSnapshotQaSpecClaims({ productId, productType });
    const { isSuccess, data } = response;
    if (!isSuccess) return null;

    return data;
  };

  const query = useQuery({
    queryKey: getQueryKeyQaClaims(productId, productType),
    queryFn: getSnapshotQaClaimsData,
    enabled,
    ...restProps,
  });

  const invalidateSnapshotQaSpecClaims = () => {
    queryClient.invalidateQueries({
      queryKey: getQueryKeyQaClaims(productId),
    });
  };

  return {
    ...query,
    snapshotObjectClaims: useMemo(() => {
      return generateFormValuesClaims(query.data?.claims || []);
    }, [query?.data]),
    invalidateSnapshotQaSpecClaims,
  };
};


export const useGetQaClaimsFormDefine = ({
  supplierModifiedData,
  snapshotData,
  requiredFields,
  setupOptionalClaims = []
}) => {
  const checkFieldRequired = (fieldName) => {
    if (isEmpty(requiredFields)) return false;
    const downCasedFieldName = fieldName.toLowerCase();
    const isNormalFieldRequired = requiredFields.includes(
      `qaspecification.qaspecclaims.${downCasedFieldName}`
    );
    const isTdcFieldRequired = requiredFields.includes(
      `tdcproperties.${downCasedFieldName}`
    );
    return isNormalFieldRequired || isTdcFieldRequired;
  };

  const getRequiredRule = ({ fieldName, displayName }) => ({
    required: checkFieldRequired(fieldName),
    message: (displayName || 'This field') + ' is required',
  });

  const claimsEnumOptions = useGetEnumOptions({
    optionMapping: useMemo(
      () => (option) => ({
        value: option.enumDisplayName,
        displayName: option.enumDisplayName,
      }),
      []
    ),
  })?.claims;

  const fields = [];
  //remove true if not needed 
  if (setupOptionalClaims.includes('Contains')) {
    fields.push({
      name: 'contains',
      label: 'Contains',
      fieldPath: 'qaSpecification.claims.contains',
      rules: [getRequiredRule({ fieldName: 'contains', displayName: 'Contains' })],
      type: 'select',
      selectProps: {
        inputProps: { mode: 'multiple', allowClear: true },
        options: sortBy(claimsEnumOptions.contains, ['displayName']),
      },
      snapshotData: snapshotData?.['contains'],
      supplierModifiedData: supplierModifiedData?.['contains'],
      renderPopoverContent: renderPopoverContent(
        'Contains',
        snapshotData?.['contains'],
        supplierModifiedData?.['contains']
      ),
    });
  }
  //remove true if not needed
  if (setupOptionalClaims.includes('Does Not Contain')) {
    fields.push({
      name: 'doesNotContain',
      label: 'Does Not Contain',
      fieldPath: 'qaSpecification.claims.doesNotContain',
      rules: [getRequiredRule({ fieldName: 'doesNotContain', displayName: 'Does Not Contain' })],
      type: 'select',
      selectProps: {
        inputProps: { mode: 'multiple', allowClear: true },
        options: sortBy(claimsEnumOptions.doesNotContain, ['displayName']),
      },
      snapshotData: snapshotData?.['doesNotContain'],
      supplierModifiedData: supplierModifiedData?.['doesNotContain'],
      renderPopoverContent: renderPopoverContent(
        'Does Not Contain',
        snapshotData?.['doesNotContain'],
        supplierModifiedData?.['doesNotContain']
      ),
    });
  }

  const formDefine = [
    [
      { rowConfig: { gutter: [15, 15] } },
      [{ colProps: { style: { flex: 1.2 } } }, ...fields]
    ],
  ];

  return formDefine;
};